import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import {
  BookOpen,
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Inline notice helper
// ─────────────────────────────────────────────────────────────────────────────
const Notice = ({ type, message }) => {
  if (!message) return null;
  const styles = {
    success:
      "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
    error:
      "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
    info: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300",
  };
  return (
    <div
      className={`flex items-start gap-2 border rounded-lg px-4 py-3 text-sm ${styles[type] || styles.info}`}
    >
      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
      <span>{message}</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Single hadith row — inline edit
// ─────────────────────────────────────────────────────────────────────────────
const HadithRow = ({ hadith, onSave, onDelete, saving, deleting }) => {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState({
    theme: hadith.theme || "",
    text: hadith.text || "",
    reference: hadith.reference || "",
    is_active: hadith.is_active ?? true,
  });
  const [dirty, setDirty] = useState(false);

  const set = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave(hadith.id, draft);
    setDirty(false);
  };

  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all ${
        hadith.is_active
          ? "border-gray-200 dark:border-gray-700"
          : "border-gray-100 dark:border-gray-800 opacity-60"
      }`}
    >
      {/* Row header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800
                           hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
      >
        <span
          className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30
                                 text-orange-600 dark:text-orange-400 text-sm font-black
                                 flex items-center justify-center"
        >
          {hadith.week_number}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {draft.theme || (
              <span className="text-gray-400 italic">No theme</span>
            )}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
            {draft.reference}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!hadith.is_active && (
            <span
              className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400
                                         px-2 py-0.5 rounded-full"
            >
              Hidden
            </span>
          )}
          {dirty && (
            <span
              className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400
                                         px-2 py-0.5 rounded-full"
            >
              Unsaved
            </span>
          )}
          {expanded ? (
            <ChevronUp size={16} className="text-gray-400" />
          ) : (
            <ChevronDown size={16} className="text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded edit form */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-700 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                Theme / Title
              </label>
              <input
                type="text"
                value={draft.theme}
                onChange={(e) => set("theme", e.target.value)}
                placeholder="e.g. Spreading Peace & Love"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                                           rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                           focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                Reference
              </label>
              <input
                type="text"
                value={draft.reference}
                onChange={(e) => set("reference", e.target.value)}
                placeholder="e.g. Book 14, Hadith 260"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                                           rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                           focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
              Hadith Text
            </label>
            <textarea
              rows={4}
              value={draft.text}
              onChange={(e) => set("text", e.target.value)}
              placeholder="Enter the hadith text..."
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                                       rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                       focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
                className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Visible on homepage
              </span>
            </label>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onDelete(hadith.id)}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                                           text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20
                                           rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 size={13} />
                Delete
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold
                                           bg-orange-500 hover:bg-orange-600 text-white rounded-lg
                                           transition-colors disabled:opacity-50"
              >
                <Save size={13} />
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add new hadith form
// ─────────────────────────────────────────────────────────────────────────────
const AddHadithForm = ({ seasonId, nextWeek, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState({
    week_number: nextWeek,
    theme: "",
    text: "",
    reference: "",
    is_active: true,
  });

  const set = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleAdd = async () => {
    if (!form.text.trim()) {
      setNotice({ type: "error", message: "Hadith text is required." });
      return;
    }
    setSaving(true);
    setNotice(null);
    const { data, error } = await supabase
      .from("weekly_hadiths")
      .insert({ ...form, season_id: seasonId })
      .select()
      .single();

    if (error) {
      setNotice({ type: "error", message: error.message });
    } else {
      onAdded(data);
      setForm({
        week_number: nextWeek + 1,
        theme: "",
        text: "",
        reference: "",
        is_active: true,
      });
      setOpen(false);
      setNotice(null);
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed
                           border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium
                           text-gray-500 dark:text-gray-400 hover:border-orange-300 dark:hover:border-orange-600
                           hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
      >
        <Plus size={16} />
        Add Week {nextWeek} Hadith
      </button>
    );
  }

  return (
    <div className="border-2 border-orange-200 dark:border-orange-800 rounded-xl p-4 space-y-3 bg-orange-50/30 dark:bg-orange-900/10">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-gray-900 dark:text-white">
          New Hadith
        </h4>
        <button
          onClick={() => setOpen(false)}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          Cancel
        </button>
      </div>

      {notice && <Notice type={notice.type} message={notice.message} />}

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
            Week #
          </label>
          <input
            type="number"
            min="1"
            value={form.week_number}
            onChange={(e) => set("week_number", parseInt(e.target.value))}
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                                   rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                   focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
            Theme / Title
          </label>
          <input
            type="text"
            value={form.theme}
            onChange={(e) => set("theme", e.target.value)}
            placeholder="e.g. Spreading Peace & Love"
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                                   rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                   focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
          Hadith Text *
        </label>
        <textarea
          rows={4}
          value={form.text}
          onChange={(e) => set("text", e.target.value)}
          placeholder="Enter the hadith text..."
          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                               rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                               focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
          Reference
        </label>
        <input
          type="text"
          value={form.reference}
          onChange={(e) => set("reference", e.target.value)}
          placeholder="e.g. Book 14, Hadith 260"
          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                               rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                               focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Visible on homepage
          </span>
        </label>
        <button
          onClick={handleAdd}
          disabled={saving}
          className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold
                               bg-orange-500 hover:bg-orange-600 text-white rounded-lg
                               transition-colors disabled:opacity-50"
        >
          <Plus size={14} />
          {saving ? "Adding…" : "Add Hadith"}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────
const WeeklyHadithPanel = () => {
  const { activeSeason } = useAdmin();
  const [hadiths, setHadiths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ── Which week is currently active (based on season start date + today) ──
  const currentWeek = (() => {
    if (!activeSeason?.start_date) return null;
    const start = new Date(activeSeason.start_date);
    const now = new Date();
    const diff = Math.floor((now - start) / (7 * 24 * 60 * 60 * 1000)) + 1;
    return Math.max(1, diff);
  })();

  const load = useCallback(async () => {
    if (!activeSeason?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("weekly_hadiths")
      .select("*")
      .eq("season_id", activeSeason.id)
      .order("week_number", { ascending: true });

    if (error) {
      setNotice({
        type: "error",
        message: "Failed to load hadiths: " + error.message,
      });
    } else {
      setHadiths(data || []);
    }
    setLoading(false);
  }, [activeSeason?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (id, draft) => {
    setSavingId(id);
    setNotice(null);
    const { error } = await supabase
      .from("weekly_hadiths")
      .update(draft)
      .eq("id", id);

    if (error) {
      setNotice({ type: "error", message: "Save failed: " + error.message });
    } else {
      setHadiths((prev) =>
        prev.map((h) => (h.id === id ? { ...h, ...draft } : h)),
      );
      setNotice({ type: "success", message: "Hadith saved." });
      setTimeout(() => setNotice(null), 3000);
    }
    setSavingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this hadith? This cannot be undone.")) return;
    setDeletingId(id);
    const { error } = await supabase
      .from("weekly_hadiths")
      .delete()
      .eq("id", id);
    if (error) {
      setNotice({ type: "error", message: "Delete failed: " + error.message });
    } else {
      setHadiths((prev) => prev.filter((h) => h.id !== id));
    }
    setDeletingId(null);
  };

  const handleAdded = (newHadith) => {
    setHadiths((prev) =>
      [...prev, newHadith].sort((a, b) => a.week_number - b.week_number),
    );
    setNotice({
      type: "success",
      message: `Week ${newHadith.week_number} hadith added.`,
    });
    setTimeout(() => setNotice(null), 3000);
  };

  const nextWeek =
    hadiths.length > 0 ? Math.max(...hadiths.map((h) => h.week_number)) + 1 : 1;

  if (!activeSeason) {
    return (
      <div className="text-center py-12 text-gray-400 dark:text-gray-500">
        No active season selected.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen size={20} className="text-orange-500" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Weekly Hadiths
            </h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Al-Adab Al-Mufrad reminders shown on the homepage each week. Season:{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {activeSeason.name}
            </span>
            {currentWeek && (
              <span className="ml-2 text-orange-500 font-semibold">
                · Week {currentWeek} active
              </span>
            )}
          </p>
        </div>
        <span className="flex-shrink-0 text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full">
          {hadiths.length} week{hadiths.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Info banner */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
        <p className="text-sm text-emerald-800 dark:text-emerald-300">
          <strong>How it works:</strong> The homepage automatically displays the
          hadith for the current week (calculated from the season start date).
          Hadiths marked as hidden won't appear publicly. Each season has its
          own independent set of hadiths.
        </p>
      </div>

      {notice && <Notice type={notice.type} message={notice.message} />}

      {/* Hadith list */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-14 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse"
            />
          ))}
        </div>
      ) : hadiths.length === 0 ? (
        <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm">
          No hadiths yet for {activeSeason.name}. Add the first one below.
        </div>
      ) : (
        <div className="space-y-2">
          {hadiths.map((h) => (
            <HadithRow
              key={h.id}
              hadith={h}
              onSave={handleSave}
              onDelete={handleDelete}
              saving={savingId === h.id}
              deleting={deletingId === h.id}
            />
          ))}
        </div>
      )}

      {/* Add new */}
      {!loading && (
        <AddHadithForm
          seasonId={activeSeason.id}
          nextWeek={nextWeek}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
};

export default WeeklyHadithPanel;
